import { addDays, startOfToday } from 'date-fns';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  checkAvailability,
  setCanBeBooked,
  setDates,
  setError,
  toggleCalendarModal,
} from 'shared/actions/dailyrentBookingCalendar';
import { selectOfferId } from 'shared/selectors';
import {
  selectDailyrentBookingCalendarAvailabilityLoading,
  selectDailyrentBookingCalendarDates,
  selectDailyrentBookingCalendarDaysAvailability,
  selectDailyrentBookingCalendarGuestsCount,
  selectDailyrentBookingCalendarInitialLoading,
  selectDailyrentBookingCalendarOpen,
} from 'shared/selectors/dailyrentBookingCalendar';
import { trackCalendarDatesSubmit } from 'shared/tracking/dailyrentBookingCalendar';

import { ICalendarContainerProps } from '../../../../packages/Calendar/shared/containers/CalendarContainer/types';
import {
  getNormalizedEndDate,
  getValidationErrors,
  formatDate,
  updateDailyrentUrlParameters,
  getNormalizedInitialDates,
  getDayStatus,
} from '../helpers';

export const usePreparedBookingCalendarData = (): ICalendarContainerProps => {
  const open = useSelector(selectDailyrentBookingCalendarOpen);
  const daysAvailability = useSelector(selectDailyrentBookingCalendarDaysAvailability);
  const dates = useSelector(selectDailyrentBookingCalendarDates);
  const initialLoading = useSelector(selectDailyrentBookingCalendarInitialLoading);
  const availabilityLoading = useSelector(selectDailyrentBookingCalendarAvailabilityLoading);
  const offerId = useSelector(selectOfferId);
  const guestsCount = useSelector(selectDailyrentBookingCalendarGuestsCount);

  const loading = initialLoading || availabilityLoading;

  const dispatch = useDispatch();
  const [minDate] = React.useState(startOfToday());
  const maxDate = React.useMemo(
    () => addDays(minDate, daysAvailability.length || 365),
    [daysAvailability.length, minDate],
  );

  const { normalizedInitialStart, normalizedInitialEnd } = getNormalizedInitialDates(minDate, dates, daysAvailability);
  const [start, setStartDate] = React.useState<Date | null>(normalizedInitialStart);
  const [end, setEndDate] = React.useState<Date | null>(normalizedInitialEnd);

  React.useEffect(() => {
    if (open) {
      const { normalizedInitialStart, normalizedInitialEnd } = getNormalizedInitialDates(
        minDate,
        dates,
        daysAvailability,
      );

      setStartDate(normalizedInitialStart);
      setEndDate(normalizedInitialEnd);
    }
  }, [open]);

  const getDayStatusDecorator = React.useCallback(
    (day: Date) => getDayStatus(daysAvailability, day),
    [daysAvailability],
  );

  const onSubmit = React.useCallback(
    async (start: Date | null, end: Date | null) => {
      if (!start || !end) {
        return;
      }

      const formattedFrom = formatDate(start);
      const formattedTo = formatDate(end);

      const newUrl = updateDailyrentUrlParameters({
        currentUrl: window.location.href,
        dates: {
          checkin: formattedFrom,
          checkout: formattedTo,
        },
        guestsCount,
      });

      const errorMessage = await dispatch(checkAvailability({ dates: { start: formattedFrom, end: formattedTo } }));

      if (errorMessage) {
        return;
      }

      history.replaceState(undefined, '', newUrl);
      dispatch(toggleCalendarModal(false));
      dispatch(setDates({ from: formattedFrom, to: formattedTo }));
      dispatch(setCanBeBooked(true));
      trackCalendarDatesSubmit(offerId);
    },
    [offerId, guestsCount],
  );

  const onChange = React.useCallback(
    (startDate: Date | undefined, endDate: Date | undefined, selectedDatesCount: number) => {
      dispatch(setError(''));
      const normalizedEndDate =
        startDate && endDate ? getNormalizedEndDate(minDate, startDate, endDate, daysAvailability) : null;
      const errors = getValidationErrors({
        fromValue: startDate || null,
        toValue: normalizedEndDate,
        daysAvailability,
      });
      const instantError = errors.find(({ isBlocker }) => !isBlocker);
      const blockerError = errors.find(({ isBlocker }) => isBlocker);

      if (!startDate || !endDate) {
        return;
      }

      setStartDate(startDate);
      setEndDate(normalizedEndDate);

      const areBothDatesSelected = selectedDatesCount === 2 && startDate.valueOf() !== normalizedEndDate?.valueOf();

      if (!areBothDatesSelected) {
        return dispatch(setError(instantError?.errorText || ''));
      } else if (blockerError?.errorText) {
        return dispatch(setError(blockerError?.errorText || ''));
      }

      return onSubmit(startDate, normalizedEndDate);
    },
    [daysAvailability, minDate, onSubmit],
  );

  return React.useMemo(
    () => ({
      minDate,
      maxDate,
      onChange,
      getDayStatus: getDayStatusDecorator,
      loading,
      fromValue: start,
      toValue: end,
    }),
    [minDate, maxDate, onChange, getDayStatusDecorator, loading, start, end],
  );
};
